<script lang="ts">
    import { getCachedEntry } from "../../api"
    import NotFound from "./NotFound.svelte"
    import ContentBlock from "../pagebuilder/ContentBlock.svelte"
    import Index from "../SEO/Index.svelte"
    import SidebarNavigation, {
        shouldShowContentSidebar,
    } from "../ecommerce/product/preview/widgets/SidebarNavigation.svelte"
    import { navigationIdToPathHierarchyMap } from "../../store"

    let { location, id, previewContent, forceId = false }: ContentComponentProps = $props()

    let loading = $state(true)
    let contentEntry: ContentEntry<"page"> = $state()
    async function loadContent() {
        loading = true
        if (previewContent) {
            contentEntry = previewContent as ContentEntry<"page">
            loading = false
            return
        }

        const currentPath = location?.path
        let requestedFilter: { [key: string]: any } | null = null

        if ((forceId || !currentPath) && id) {
            requestedFilter = { _id: id }
        } else if (currentPath) {
            let resolvedPath = currentPath.replace(/\/$/, "")

            requestedFilter = {
                $or: [{ "generalDetails.path": resolvedPath }, { "metaDetails.alternativePaths": resolvedPath }],
            }
        }

        try {
            contentEntry = requestedFilter
                ? ((await getCachedEntry("content", requestedFilter)) as ContentEntry<"page">)
                : undefined
        } catch (e) {
            console.error(e)
        }
        loading = false
    }

    let _location: typeof location = location
    let _id: typeof id = id
    let hasPageTitleBlock = $derived(contentEntry?.blocks?.some((block) => block.type === "pageTitle") || false)
    let fallbackHeading = $derived(contentEntry?.generalDetails?.name || contentEntry?.metaDetails?.title || "")
    // Listing-Seiten (ProductList-Block) bringen ihre eigene SidebarNavigation mit –
    // dort keine zusätzliche generische linke Navi rendern.
    let hasOwnSidebar = $derived(
        (contentEntry?.blocks || []).some(
            (block) => (block.type as string) === "productList" || (block.type as string) === "productListBlock"
        )
    )
    let showSidebar = $derived(
        !!contentEntry &&
            !hasOwnSidebar &&
            shouldShowContentSidebar($navigationIdToPathHierarchyMap, contentEntry.id)
    )

    // isArticle aus metaDetails?.isArticle bei Bedarf an <Index article={...}> übergeben.
    // Dazu in Index.svelte, OpenGraph.svelte und SchemaORG.svelte die article-Props einkommentieren.

    $effect(() => {
        if (location?.path != _location?.path || id != _id) {
            _id = id
            _location = location
            loadContent()
        }
    })

    loadContent()
</script>

{#if loading}{:else if contentEntry}
    <Index
        createdAt={new Date(contentEntry.insertTime)}
        updatedAt={new Date(contentEntry.updateTime)}
        metaDescription={contentEntry.metaDetails?.description}
        metaKeywords={contentEntry.metaDetails?.keywords}
        title={contentEntry.metaDetails?.title}
        FAQ={contentEntry.metaDetails?.hasFAQ}
        FAQDetails={contentEntry.metaDetails?.FAQ}
        noIndex={contentEntry.metaDetails?.noIndex}
        canonicalPath={contentEntry.generalDetails?.path}
    />
    {#if !hasPageTitleBlock && fallbackHeading}
        <h1 class="seo-fallback-h1">{fallbackHeading}</h1>
    {/if}
    {#if showSidebar}
        <div class="content-with-sidebar">
            <aside class="content-sidebar">
                <SidebarNavigation pageId={contentEntry.id} showCounts={false} />
            </aside>
            <div class="content-block-wrapper">
                {#each contentEntry.blocks as block, idx}
                    <ContentBlock {block} {contentEntry} />
                {/each}
            </div>
        </div>
    {:else}
        <div class="content-block-wrapper">
            {#each contentEntry.blocks as block, idx}
                <ContentBlock {block} {contentEntry} />
            {/each}
        </div>
    {/if}
{:else}
    <NotFound />
{/if}

<style lang="less" global>
    @import "../../css/variables.less";
    .seo-fallback-h1 {
        position: absolute;
        width: 1px;
        height: 1px;
        padding: 0;
        margin: -1px;
        overflow: hidden;
        clip: rect(0, 0, 0, 0);
        white-space: nowrap;
        border: 0;
    }

    .content-block-wrapper {
        display: flex;
        flex-direction: column;
        align-items: center;
        width: 100%;
    }

    .content-with-sidebar {
        display: flex;
        width: 100%;
        max-width: var(--max-container-width);
        margin: 0 auto;
        padding: 0 1.2rem;
        box-sizing: border-box;
        gap: 2.4rem;
        align-items: flex-start;
    }
    .content-sidebar {
        width: 317px;
        min-width: 317px;
        align-self: flex-start;
        position: sticky;
        top: calc(var(--header-height, 104px) + 2.4rem);
        max-height: calc(100vh - (var(--header-height, 104px) + 2.4rem));
        overflow-y: auto;
    }
    .content-with-sidebar .content-block-wrapper {
        flex: 1;
        min-width: 0;
    }
    @media @max-mobile {
        .content-with-sidebar {
            flex-direction: column;
        }
        .content-sidebar {
            display: none;
        }
    }
</style>
